// src/utils/noiseFormulas.js

/**
 * Constantes de Normativas Internacionales
 */
export const NOISE_REGS = {
  niosh: { 
    name: "NIOSH / ACGIH", 
    crit: 85, 
    action: 85, 
    exrate: 3, 
    label: "TWA",
    desc: "Basado en criterios científicos de salud para prevenir la pérdida auditiva. Utiliza una tasa de intercambio de 3dB y un límite estricto de 85dB. Recomendado para excelencia en SSO."
  },
  eu: { 
    name: "ISO / Europa / ES", 
    crit: 85, 
    action: 80, 
    exrate: 3, 
    label: "LEP,d",
    desc: "Normativa basada en la ISO 9612. Utiliza una tasa de intercambio de 3dB y establece un Nivel de Acción inferior (80dB) para iniciar medidas preventivas técnicas."
  },
  osha: { 
    name: "OSHA / MSHA", 
    crit: 90, 
    action: 85, 
    exrate: 5, 
    label: "TWA",
    desc: "Estándar Federal de cumplimiento legal en EE.UU. Utiliza una tasa de intercambio de 5dB. Es el modelo más permisivo, diseñado para cumplimiento regulatorio básico." 
  }
};

/**
 * Calcula la exposición total
 */
export const calculateNoiseExposure = (segments, regKey = 'niosh') => {
  const reg = NOISE_REGS[regKey];
  let totalDose = 0;
  let totalSeconds = 0;

  const processedSegments = segments.map(s => {
    const L = parseFloat(s.db) || 0;
    const h = parseFloat(s.hours) || 0;
    const m = parseFloat(s.minutes) || 0;
    const timeInSeconds = (h * 3600) + (m * 60);

    if (L >= 40 && timeInSeconds > 0) {
      // Fórmula Maestra: Tiempo Permitido (T) = 8 / 2^((L-crit)/ER)
      const allowedTimeHours = 8 / Math.pow(2, (L - reg.crit) / reg.exrate);
      const allowedTimeSeconds = allowedTimeHours * 3600;
      
      const partialDose = (timeInSeconds / allowedTimeSeconds);
      totalDose += partialDose;
      totalSeconds += timeInSeconds;

      return { ...s, partialDose: (partialDose * 100).toFixed(2) };
    }
    return { ...s, partialDose: "0.00" };
  });

  // TWA/LEPd = (ER / log10(2)) * log10(Dose/100) + crit
  // Simplificado para ER=3 -> 10 * log10(Dose) + 85
  // Simplificado para ER=5 -> 16.61 * log10(Dose) + 90
  const logConstant = reg.exrate / Math.log10(2);
  const twa = totalDose > 0 
    ? (logConstant * Math.log10(totalDose) + reg.crit).toFixed(1)
    : "0.0";

  return {
    dose: (totalDose * 100).toFixed(1),
    twa: parseFloat(twa),
    totalTime: (totalSeconds / 3600).toFixed(2),
    label: reg.label,
    status: twa >= reg.crit ? "Peligro" : twa >= reg.action ? "Precaución" : "Seguro",
    segments: processedSegments
  };
};

/**
 * Texto de recomendación legal/técnica.
 * @param {number} twa
 * @param {string} regKey
 * @param {{ seguro?: string, accion?: string, excedido?: string } | null} [diagnostics]
 */
export const getNoiseActionText = (twa, regKey, diagnostics = null) => {
  const reg = NOISE_REGS[regKey];
  const d = diagnostics || {};
  if (twa >= reg.crit) {
    return (
      d.excedido ||
      "Nivel EXCEDIDO. Uso de protección auditiva (EPP) OBLIGATORIO. Se requiere implementación inmediata de controles de ingeniería."
    );
  }
  if (twa >= reg.action) {
    return (
      d.accion ||
      "Nivel de ACCIÓN alcanzado. Se recomienda vigilancia de la salud (audiometrías), capacitación y uso de EPP."
    );
  }
  return (
    d.seguro ||
    "Nivel SEGURO. No se requieren acciones correctivas inmediatas. Continuar con monitoreo preventivo."
  );
};
/*<!--© 2026 Todos los derechos reservados Desarrollado por JEMAprod-Tecnologia-->*/